/**
 * Scroll to a name (anchor) on the page with smooth animation.
 *
 */

import { gsap } from 'gsap'
// import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/all'

gsap.registerPlugin(ScrollToPlugin)

document.addEventListener('DOMContentLoaded', () => {
	// window.addEventListener('load', () => {
	const aNames = document.querySelectorAll('a[href*="#"]')
	if (!aNames.length) return
	let activeLenisTween = null

	const getTargetScrollY = ({ target, offset = 0 }) => {
		if (typeof target === 'number') {
			return Math.max(0, target)
		}

		const targetElement = typeof target === 'string' ? document.querySelector(target) : target
		if (!targetElement) return null

		// offsetTop traversal: stable regardless of GSAP/ScrollTrigger pin-spacers or
		// scroll-triggered animations on surrounding elements (getBoundingClientRect shifts)
		let absoluteTop = 0
		let cur = targetElement
		while (cur) {
			absoluteTop += cur.offsetTop
			cur = cur.offsetParent
		}
		return Math.max(0, absoluteTop - (offset ?? 0))
	}

	const scrollWithEngine = ({ target, offset, onComplete }) => {
		const targetY = getTargetScrollY({ target, offset: offset ?? 0 })
		if (targetY === null) return

		if (window.lenis && typeof window.lenis.scrollTo === 'function') {
			if (activeLenisTween) activeLenisTween.kill()

			const tweenState = {
				y: window.lenis.animatedScroll ?? window.lenis.actualScroll ?? window.scrollY,
			}

			activeLenisTween = gsap.to(tweenState, {
				y: targetY,
				duration: 1.0,
				ease: 'power3.out',
				overwrite: 'auto',
				onUpdate: () => {
					window.lenis.scrollTo(tweenState.y, { immediate: true, force: true })
				},
				onComplete: () => {
					activeLenisTween = null
					if (typeof onComplete === 'function') onComplete()
				},
				onInterrupt: () => {
					activeLenisTween = null
				},
			})
			return
		}

		// Disable CSS scroll-behavior during GSAP animation: scroll-behavior:smooth on <html>
		// causes each window.scrollTo() frame call to trigger a micro CSS smooth-scroll,
		// producing a visible jump at the start of the animation.
		document.documentElement.style.scrollBehavior = 'auto'
		gsap.to(window, {
			duration: 1.3,
			scrollTo: { y: targetY },
			ease: 'power3.out',
			onComplete: () => {
				document.documentElement.style.scrollBehavior = ''
				if (typeof onComplete === 'function') onComplete()
			},
		})
	}

	const normalizePath = path => path.replace(/\/$/, '') || '/'

	const linkToSamePage = linkElement => {
		return linkElement.origin === location.origin && normalizePath(linkElement.pathname) === normalizePath(location.pathname)
	}

	const resolveCardPadding = () => {
		const el = document.createElement('div')
		el.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;padding:var(--card-padding)'
		document.body.appendChild(el)
		const value = parseFloat(getComputedStyle(el).paddingTop) || 0
		document.body.removeChild(el)
		return value
	}

	const getScrollOffset = () => {
		const masthead = document.querySelector('#masthead')?.getBoundingClientRect() ?? { height: 0 }
		return (masthead.height ?? 0) + resolveCardPadding()
	}

	const offset = getScrollOffset()

	// CSS variable for browser-native hash scroll (first approximation via scroll-margin-top)
	document.documentElement.style.setProperty('--scroll-offset', `${offset}px`)

	// Hash on load: temporarily strip the id so the browser can't find the anchor and
	// does no native hash scroll; restore it and animate smoothly after window.load.
	if (window.location.hash) {
		const hashTarget = window.location.hash.slice(1)
		const targetEl = document.getElementById(hashTarget)

		if (targetEl) {
			targetEl.removeAttribute('id')

			const animateToHash = () => {
				targetEl.id = hashTarget
				scrollWithEngine({
					target: targetEl,
					offset: getScrollOffset(),
				})
			}

			if (document.readyState === 'complete') {
				requestAnimationFrame(animateToHash)
			} else {
				window.addEventListener('load', () => requestAnimationFrame(animateToHash), { once: true })
			}
		}
	}

	aNames.forEach(a => {
		const hrefValue = a.getAttribute('href')

		if (hrefValue.split('#')[1].length && linkToSamePage(a)) {
			a.addEventListener('click', e => {
				e.preventDefault()
				const href = e.currentTarget.getAttribute('href')
				if (!href || !href.includes('#')) return
				const target = href.split('#')[1]

				const filteredTarget = target === 'top' ? false : target

				if (document.querySelector(`#${target}`) || !filteredTarget) {
					scrollWithEngine({
						target: filteredTarget ? `#${filteredTarget}` : 0,
						offset: getScrollOffset(),
						onComplete: () => {
							history.pushState(null, null, `#${target}`)
						},
					})
				}
			})
		}
	})

	// Same-page links without hash: scroll to top instead of reloading.
	// Uses event delegation so linkToSamePage is evaluated at click time (URL may have
	// changed since DOMContentLoaded via history.pushState).
	document.addEventListener('click', e => {
		const a = e.target.closest('a[href]')
		if (!a) return
		const href = a.getAttribute('href')
		if (!href || href.includes('#')) return
		if (!linkToSamePage(a)) return

		e.preventDefault()
		scrollWithEngine({
			target: 0,
			offset: 0,
			onComplete: () => {
				history.pushState(null, null, href)
			},
		})
	})
})
